import React, { useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { AppHeader } from '@/components/AppHeader.jsx';
import { MainLayout } from '@/components/MainLayout.jsx';
import { MobileLayout } from '@/components/mobile/MobileLayout.jsx';
import MobileErrorBoundary from '@/components/mobile/MobileErrorBoundary.jsx';
import { AudioProvider, useAudioContext } from '@/contexts/AudioContext.jsx';
import { WelcomeProvider, useWelcome } from '@/contexts/WelcomeContext.jsx';
import { WelcomeModal } from '@/components/WelcomeModal.jsx';
import { MaintenancePage } from '@/components/MaintenancePage.jsx';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';

const VITE_MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

function AppWithHotkeys() {
  const {
    handlePlayPause,
    isStreamerMode,
    setIsStreamerMode,
    togglePlaylist,
    playPrevious,
    playNext,
    isPlayerMinimized,
    setIsPlayerMinimized,
  } = useAudioContext();

  const { showWelcome, closeWelcome } = useWelcome();
  const { isMobile } = useDeviceDetection();

  // Log for debugging on mobile
  useEffect(() => {
    console.log('App render - isMobile:', isMobile, 'width:', window.innerWidth);
  }, [isMobile]);

  useEffect(() => {
    function onKeyDown(e) {
      // Ignore if focus is on input, textarea, or contenteditable
      const tag = document.activeElement?.tagName;
      const isEditable = document.activeElement?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || isEditable) return;

      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        handlePlayPause();
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        setIsStreamerMode && setIsStreamerMode(!isStreamerMode);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        togglePlaylist && togglePlaylist();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        playPrevious && playPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        playNext && playNext();
      } else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setIsPlayerMinimized && setIsPlayerMinimized(!isPlayerMinimized);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePlayPause, isStreamerMode, setIsStreamerMode, togglePlaylist, playPrevious, playNext, isPlayerMinimized, setIsPlayerMinimized]);
  
  // Add class to hide scrollbar in Electron when in streamer mode
  useEffect(() => {
    if (isStreamerMode) {
      document.body.classList.add('streamer-mode');
    } else {
      document.body.classList.remove('streamer-mode');
    }
  }, [isStreamerMode]);
// Mobile layout - simplified interface for phones
  if (isMobile) {
    return (
      <MobileErrorBoundary>
        <MobileLayout />
        <Toaster />
        <WelcomeModal isOpen={showWelcome} onClose={closeWelcome} />
      </MobileErrorBoundary>
    );
  }

  // Desktop layout - full featured interface
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
      <AppHeader />
      <MainLayout />
      <Toaster />
      <WelcomeModal isOpen={showWelcome} onClose={closeWelcome} />
    </div>
  );
}

function App() {
  // Check for maintenance mode via environment variable or URL path
  const isMaintenanceMode = VITE_MAINTENANCE_MODE || window.location.pathname === '/maintenance';
  
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }
  return (
    <WelcomeProvider>
      <AudioProvider>
        <AppWithHotkeys />
      </AudioProvider>
    </WelcomeProvider>
  );
}

export default App;